import {
  ConfirmDialog,
  Dialog,
  InvalidInput,
  type Plan,
  type Traveller,
  type TravellerInput,
} from '@trippath/shared';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { problemKey } from '../../lib/format';

interface Props {
  plan: Plan;
  /** The traveller as they are, when editing; a new one starts empty. */
  traveller?: Traveller;
  onClose: () => void;
  save: (input: TravellerInput) => Promise<Plan>;
  remove?: () => Promise<Plan>;
  onSaved: (plan: Plan) => void;
}

/**
 * Adds, edits or removes a fellow traveller: a name, one of the trip's people to name on a
 * ticket. Which ways they go is said on each ticket's travellers, not here; a new one is along
 * for the whole trip, one kept keeps their days.
 */
export function TravellerDialog({ plan, traveller, onClose, save, remove, onSaved }: Props) {
  const { t } = useTranslation();
  const ids = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(traveller?.name ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);

  const done = (job: Promise<Plan>) => {
    job.then(onSaved, (error: unknown) => {
      // Only its name is typed here: a problem with anything else is saving that failed.
      const named =
        error instanceof InvalidInput ? error.fields.filter((f) => f.field === 'name') : [];
      if (named.length > 0) {
        setErrors(Object.fromEntries(named.map((f) => [f.field, t(problemKey(f.problem))])));
      } else {
        setFailed(true);
      }
    });
  };

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const missing: Record<string, string> = {};
    if (name.trim() === '') missing.name = t('problem.required');
    setErrors(missing);
    setFailed(false);
    if (Object.keys(missing).length > 0) return;
    done(
      save({
        name,
        joins: traveller?.joins ?? plan.trip.start,
        leaves: traveller?.leaves ?? plan.trip.end,
        // Their own legs said before stay as they were; new ones are the plan's.
        legs: (traveller?.legs ?? []).map((leg) => ({
          day: leg.day,
          way: leg.way,
          price: leg.price?.minor ?? null,
          reference: leg.reference,
        })),
      }),
    );
  };

  const title = traveller
    ? t('travellerForm.editTitle', { name: traveller.name })
    : t('travellerForm.newTitle');

  return (
    <>
      <Dialog open={!removing} onClose={onClose} title={title} initialFocus={first}>
        <form className="form" onSubmit={submit} noValidate>
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-name`} data-required>
              {t('travellerForm.name')}
            </label>
            <input
              id={`${ids}-name`}
              ref={first}
              className="text-input"
              type="text"
              value={name}
              aria-invalid={errors.name !== undefined}
              aria-describedby={errors.name === undefined ? undefined : `${ids}-name-error`}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            {errors.name !== undefined && (
              <p className="field-error" id={`${ids}-name-error`}>
                {errors.name}
              </p>
            )}
          </div>

          {failed && (
            <p className="form-failed" role="alert">
              {t('plan.failed')}
            </p>
          )}
          <div className="dialog-actions">
            {remove !== undefined && (
              <button
                type="button"
                className="button button-quiet dialog-action-apart"
                onClick={() => {
                  setRemoving(true);
                }}
              >
                {t('travellerForm.remove')}
              </button>
            )}
            <button type="button" className="button" onClick={onClose}>
              {t('dialog.cancel')}
            </button>
            <button type="submit" className="button button-primary">
              {traveller ? t('travellerForm.save') : t('travellerForm.add')}
            </button>
          </div>
        </form>
      </Dialog>
      {remove !== undefined && (
        <ConfirmDialog
          open={removing}
          title={t('travellerForm.removeQuestion', { name })}
          body={t('travellerForm.removeBody')}
          confirmLabel={t('travellerForm.removeConfirm')}
          onCancel={() => {
            setRemoving(false);
          }}
          onConfirm={() => {
            setRemoving(false);
            done(remove());
          }}
        />
      )}
    </>
  );
}
